<template>
	<div>
		<!-- dialog body -->
		<v-dialog v-model="visible" persistent max-width="650px">
			<v-card>
				<v-card-title class="justify-center">
					<h3> {{ title }} </h3>
					<v-btn icon absolute top right color="dark" @click="closeTaskDialog">
						<b>x</b>
					</v-btn>
				</v-card-title>

				<v-card-text>
					<v-form
						ref="form" 
						v-model="submitForm"
						@submit.prevent
					>
						<v-container fluid>
							<v-row justify="center">
								<!-- title field -->
								<v-col cols="12">
									<div class="mb-1">
										<h3 class="font-weight-medium">Title</h3>
									</div>
									<v-text-field
										outlined
										dense
										placeholder="Title"
										class="rounded-input"
										:rules="[rules.required]"
										v-model="addEditTask.title"
										maxlength="255"
										counter
										lazy
									>
									</v-text-field>
								</v-col>
							</v-row>

							<v-row justify="center" class="my-1">
								<!-- datetime picker -->
								<v-col cols="12" md="6" sm="6" align-self="center">
									<div class="mb-1">
										<h3 class="font-weight-medium">Due Date & Time</h3>
									</div>
									<input 
										name="due-date-time"
										type="datetime-local" 
										class="rounded-input datetime-input"
										title="Due Date and Time"
										required
										:rules="[rules.required]"
										:value="htmlTimeFormat(addEditTask.due_datetime)"
										@change="setTaskDueDatetime"
									>
								</v-col>

								<v-spacer></v-spacer>

								<!-- assignee dropdown -->
								<v-col cols="12" md="6" sm="6" v-if="assigneeItems" align-self="center">
									<div class="mb-1">
										<h3 class="font-weight-medium">Assign To</h3>
									</div>
									<v-autocomplete
										:disabled="!currentUser.privileges.canAssignTasks"
										dense
										filled
										solo
										:items="assigneeItems"
										item-text="full_name"
										item-value="id"
										label="Assign To"
										v-model="addEditTask.assignee"
										class="rounded-input"
										hide-details
									>
										<template v-slot:item="data">
											<template>
												<v-list-item-content>
													<v-list-item-title
														v-html="data.item.full_name"
													></v-list-item-title>
													<v-list-item-subtitle
														v-html="data.item.username"
													></v-list-item-subtitle>
												</v-list-item-content>
											</template>
										</template>
									</v-autocomplete>
								</v-col>
							</v-row>

							<v-row justify="center" v-if="showAddTags" class="my-1">
								<!-- autocomplete for tags selection -->
								<v-col cols="12" align-self="center">
									<v-autocomplete
										v-model="addEditTask.task_tags"
										:items="tagList"
										item-text="name"
										item-value="id"
										label="Select tags"
										multiple
										outlined
										class="rounded-input my-1"
										hide-details
									>
										<template v-slot:selection="{ item }">
											<v-chip
												:selection="item.selected"
												:color="item.color ? item.color : 'grey'"
											>
												<div v-if="item.name">
													{{ item.name }}
												</div>
											</v-chip>
										</template>
									</v-autocomplete>
								</v-col>
							</v-row>

							<v-row justify="center" v-if="showAddDescription" class="my-1">
								<!-- description text -->
								<v-col cols="12" align-self="center">
									<v-textarea
										outlined
										dense
										label="Description"
										v-model="addEditTask.description"
										class="rounded-input"
										auto-grow
										rows="1"
										maxlength="300"
										counter
									>
									</v-textarea>
								</v-col>
							</v-row>
							
							<!-- buttons -->
							<v-row justify="center">
								<v-col cols="12" md="4" align-self="center" v-if="!showAddDescription  && !this.type">
									<v-btn
										text
										class="text-decoration-underline blue--text"
										@click="showAddDescription = true"
										:disabled="isLoading"
									>
										+ Add Description
									</v-btn>
								</v-col>

								<v-col cols="12" md="3" v-if="!showAddTags" align-self="center">
									<v-btn
										text
										class="text-decoration-underline blue--text"
										@click="showAddTags = true"
										:disabled="isLoading"
									>
										+ Add Tags
									</v-btn>
								</v-col>

								<v-spacer></v-spacer>
								
								<v-col cols="12" :md="type ? '4' : '3'" align-self="center">
									<!-- submit btn -->
									<v-btn
										color="primary"
										type="submit"
										v-model="submitForm"
										:disabled="isLoading || !submitForm"
										@click="submitTask()"
									>
										Submit
									</v-btn>
								</v-col>
							</v-row>
						</v-container>
					</v-form>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>

	</div>
</template>

<script>
import api from "@api/index"
import { mapActions, mapGetters } from "vuex"
import endpoints from "@api/repository"
import Helper from "@utils/misc"
import moment from "moment"

export default {
	name: 'AddTaskDialog',

	components: {},
	
	props: {
		"visible": {
			type: Boolean,
			default: false,
		},
		"title": {
			type: String,
			default: 'Add Task'
		},
		// type -> task (0) or subtask(1)
		"type": {
			type: Number,
			default: 0
		},
		"task": {
			type: Object,
			default: () => {}
		},
		"is_editing": {
			type: Boolean,
			default: false,
		},
		"parentId": {
			type: Number,
			default: null
		}
	},
	
	data() {
		return {
			moment: moment,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},
			rules: {
				required: (value) => !!value || "Required",
			},
			submitForm: false,
			assigneeItems: null,
			addEditTask: {
				title: null,
				institution: null,
				task_tags: []
			},

			showAddDescription: false,
			showAddTags: false,

			tagList: [],

			detectChanges: 0,
		}
	},
	
	computed: {
		...mapGetters([
			"currentUser",
			"accessToken",
			"isLoading",
			"currentInstitution",
		]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
	},
	
	methods: {
		...mapActions(["setLoading"]),

		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.message = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		async fetchAllTags() {
			var url = endpoints.taskTags
			var response = await api.call(this.apiEssentials, url)

			if(response) {
				this.tagList = response
			}
		},

		// convert due_datetime according to html format reqd
		htmlTimeFormat(time) {
			var formattedTime = moment(time).format('YYYY-MM-DD T HH:mm').replaceAll(' ', '')
			return formattedTime
		},

		// update due_datetime according to html format reqd
		setTaskDueDatetime(event) {
			var newDueDatetime = event.target.value
			this.addEditTask.due_datetime = newDueDatetime
		},

		// fetch all staff for assignee dropdown
		async getAllStaff() {
			var response = await api.call(this.apiEssentials, endpoints.getAllStaff+'?all=true')
			this.assigneeItems = response
		},

		// replaces received assignee, tags, creator (if objects) with their ids only
		reduceObjectsToID() {
			// only if task item is received -> default keys=title,institution, hence key length must be > 2
			if(Object.keys(this.addEditTask).length > 2) {

				// if assignee data available
				if(this.addEditTask.assignee) {
					if(this.addEditTask.assignee instanceof Object) {
						this.addEditTask.assignee = this.addEditTask.assignee.id
					}
				}

				// if tags data available
				if(this.addEditTask.task_tags) {
					this.addEditTask.task_tags = this.addEditTask.task_tags.map(tag => tag.id)
				}

				// if creator data available
				if(this.addEditTask.creator) {
					if(this.addEditTask.creator instanceof Object) {
						this.addEditTask.creator = this.addEditTask.creator.id
					}
				}
			}
		},

		// validate title, due_datetime, assignee
		validate() {
			if(!this.addEditTask.title) {
				this.snackbar.message = 'Task title cannot be empty!'
				this.snackbar.visible = true
				this.snackbar.color = "error"

				return false
			}

			if(!this.addEditTask.assignee) {
				this.snackbar.message = 'Task assignee cannot be empty!'
				this.snackbar.visible = true
				this.snackbar.color = "error"

				return false
			}

			return true
		},

		// submit task via post api call
		async submitTask() {
			// set default due_datetime - current time
			if(!Object.prototype.hasOwnProperty.call(this.addEditTask, 'due_datetime')) {
				this.addEditTask.due_datetime = moment().format('YYYY-MM-DD T HH:mm').replaceAll(' ', '')
			}

			if(this.validate()) {
				this.addEditTask.is_sub_task = this.type ? true : false
				var url = endpoints.tasks
				var response

				if(this.is_editing) {
					url += `${this.addEditTask.id}/`
					response = await api.call(this.apiEssentials, url, api.Methods.PUT, this.addEditTask)
					var id = this.addEditTask.is_sub_task? this.addEditTask.parent : this.addEditTask.id;

					// emit to parent to send updated details of the task - if editing a task
					this.$emit('changeTaskInDialog', id)
				}
				else {
					if(this.addEditTask.is_sub_task){
						this.addEditTask.parent = this.parentId;
					}
					response = await api.call(this.apiEssentials, url, api.Methods.POST, this.addEditTask)

					// emit to parent to send updated details of the task - if adding a new sub-task
					if(this.addEditTask.is_sub_task)
						this.$emit('changeTaskInDialog', this.addEditTask.parent)
				}

				// on success 
				if(response) {
					if(!this.is_editing && !this.type) {
						// if new main task added, notify parent to fetch tasks again
						this.$emit('fetchNewTasks')
					}
					this.closeTaskDialog()
					this.snackbar.message = 'Task submitted successfully!'
					this.snackbar.visible = true
					this.snackbar.color = "success"
				}
			}
		},

		closeTaskDialog() {
			this.showAddDescription = false
			this.showAddTags = false
			if(!this.is_editing && !this.type){
				// emit to AllTasks to close the Add Task dialog
				this.$emit('closeAddTaskDialog')
			}else{
				// emit to TaskDetails to close the Add Task dialog - after add/edit a task/sub-task
				this.$emit('closeAddEditTaskDialog')
			}
			this.addEditTask = {
				title: null,
				institution: this.currentInstitution.id,
			},
			this.$refs.form.reset()
		},
	},

	created() {
		this.getAllStaff()
		this.fetchAllTags()
	},

	watch: {
		visible: {
			async handler() {
				if(this.visible) {
					this.addEditTask.institution = this.currentInstitution.id
					this.addEditTask.assignee = this.currentUser.id
					if(!this.addEditTask.due_datetime){
						this.addEditTask.due_datetime = moment().add(1,'hours').format('YYYY-MM-DD T HH:mm').replaceAll(' ', '')
					}
				}
			}
		},

		task: {
			async handler() {
				// if empty object i.e. new task/sub-task, initialise empty obj with title=null
				if(this.task && this.task.id){
					this.addEditTask = Helper.clone(this.task)
					// else store received obj in local obj v-modelled to fields
					if(this.task.description) {
						this.showAddDescription= true
					}

					if(this.task.task_tags.length) {
						this.showAddTags = true
					}
				}
				this.reduceObjectsToID()
			}
		}
	}
}
</script>

<style>
	.datetime-input {
		border: 1px solid grey;
		border-radius: 5px;
		outline: none;
		height: 40px;
		padding: 0 10px;
		width: 100%;
	}

	.datetime-input::placeholder {
		font-size: 50px;
	}

	input[type="datetime-local"] {
		font-size: 18px;
	}

	input[type="datetime-local"]::-webkit-calendar-picker-indicator {
		margin: 0;
		padding: 0;
	}
</style>